import { toast } from 'sonner';

/**
 * Copy text to the clipboard; false if the browser refused. Never throws. Call it from the click:
 * `navigator.clipboard` is missing over plain http (the dev box) and can refuse, and the fallback
 * only works while the click is still running.
 */
export async function copyText(text: string): Promise<boolean> {
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  // No await before this, so it still runs inside the click.
  if (!clipboard?.writeText) return copyWithCommand(text);
  try {
    await clipboard.writeText(text);
    return true;
  } catch {
    return copyWithCommand(text);
  }
}

/**
 * Copy text that is still loading; false if it failed to load or the browser refused. Safari only
 * allows a copy during the click, so the pending text goes to the clipboard as a promise; browsers
 * without that support copy once it arrives.
 */
export async function copyTextWhenReady(text: Promise<string>): Promise<boolean> {
  if (
    typeof ClipboardItem !== 'undefined' &&
    navigator.clipboard?.write &&
    ClipboardItem.supports?.('text/plain') !== false
  ) {
    try {
      const blob = text.then((t) => new Blob([t], { type: 'text/plain' }));
      await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
      return true;
    } catch {
      // Older browsers reject a promise in ClipboardItem; fall back below.
    }
  }
  try {
    return await copyText(await text);
  } catch {
    return false;
  }
}

/** Copy, then toast "Copied the link" or "Could not copy the link". */
export async function copyWithToast(text: string, what?: string): Promise<boolean> {
  const ok = await copyText(text);
  if (ok) toast.success(what ? `Copied ${what}` : 'Copied');
  else
    toast.error(what ? `Could not copy ${what}` : 'Could not copy', {
      description: 'The browser blocked the clipboard. Select the text and copy it instead.',
    });
  return ok;
}

/**
 * The older copy command, for plain http and a refused Clipboard API. It copies the selection, so
 * an off-screen span is selected, and a copy listener sets the exact text. Focus never moves, so a
 * dialog's focus trap doesn't notice.
 */
function copyWithCommand(text: string): boolean {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false;
  let ran = false;
  const onCopy = (e: ClipboardEvent) => {
    if (!e.clipboardData) return;
    e.clipboardData.setData('text/plain', text);
    e.preventDefault();
    ran = true;
  };
  const node = document.createElement('span');
  node.textContent = text;
  node.setAttribute('aria-hidden', 'true');
  node.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;white-space:pre;user-select:text;-webkit-user-select:text';
  const selection = document.getSelection();
  const previous = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  document.addEventListener('copy', onCopy, true);
  document.body.appendChild(node);
  try {
    const range = document.createRange();
    range.selectNodeContents(node);
    selection?.removeAllRanges();
    selection?.addRange(range);
    const ok = document.execCommand('copy');
    return ok && ran;
  } catch {
    return false;
  } finally {
    document.removeEventListener('copy', onCopy, true);
    node.remove();
    if (selection) {
      selection.removeAllRanges();
      if (previous) selection.addRange(previous);
    }
  }
}
